import { FormInput, HeadlessUI, NeutralButton, NeutralTypeEnum, PrimaryButton } from '@systemeio/ui'
import {
  ContactFilterSavedInterface,
  ContactFilterType,
  ContactFiltererInterface,
} from 'modules/contacts/types/contact-filter-interface'
import { getDefaultFilterValue } from 'modules/contacts/utils/get-default-filter-value'
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import ConfirmationModal from 'shared/components/confirmation-modal'
import CreateModal from 'shared/components/create-modal'
import { FilterableItemType, ListingLayoutProps } from 'shared/components/listing-layout'
import { Loader } from 'shared/components/loader'
import { BaseTableProps } from 'shared/components/table/components/base-table'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import useMediaQuery from 'shared/hooks/use-media-query'
import { DataWithPaginationInterface } from 'shared/types/pagination-interface'
import { getFullOffsetOfElement } from 'shared/utils/get-full-offset-of-element'
import { CloseIcon, FilterIcon } from '@systemeio/ui'
import { BadRequest } from '@systemeio/util'

const defaultSaveFilterValues = { name: '' }

interface ContactListingLayoutProps<T extends DataWithPaginationInterface<T['items'][0]>, K, R>
  extends Pick<
    ListingLayoutProps<T, K, R>,
    'data' | 'title' | 'children' | 'customHeaderElement' | 'counter'
  > {
  isMenuShown: boolean
  filterData: {
    filterable: FilterableItemType<ContactFilterType>[]
    applySavedFilters: (savedFilters: ContactFilterSavedInterface) => void
    onDeleteSavedFilter: (filter: ContactFilterSavedInterface) => Promise<void>
    onSaveNewFilter: (data: { name: string }) => Promise<void>
    onResetFilters: () => void
    filterer: ContactFiltererInterface
  } & Required<Pick<BaseTableProps<T>, 'paginator' | 'limiter'>>
}

const DEFAULT_INDENT = 20
const MARGIN_BOTTOM_XL = 60
const MARGIN_BOTTOM = 90
const FILTER_INSIDE_PADDING = 32

const ContactsListingLayout = <
  T extends { items: DataWithPaginationInterface<any>['items']; hasMore: boolean },
  K,
  R,
>({
  title,
  data,
  counter,
  filterData,
  children,
  customHeaderElement,
  isMenuShown,
}: ContactListingLayoutProps<T, K, R>) => {
  const { t } = useLocoTranslation()
  const { isXL } = useMediaQuery()
  const tableRef = useRef<HTMLDivElement>(null)
  const filterRef = useRef<HTMLDivElement>(null)
  const filterContainerRef = useRef<HTMLDivElement>(null)
  const [isFilterShown, setIsFilterShown] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [savedFilters, setSavedFilters] = useState<ContactFilterSavedInterface[]>([])
  const [deleteModalOpened, setDeleteModalOpened] = useState<Record<string, boolean>>({})
  const [saveModalOpened, setSaveModalOpened] = useState(false)

  const [errorSaveFilter, setErrorSaveFilter] = useState<string>()

  useEffect(() => {
    if (filterData.filterer.savedFilters) {
      setSavedFilters(filterData.filterer.savedFilters)
      setDeleteModalOpened(
        filterData.filterer.savedFilters.reduce((obj, el) => {
          return { ...obj, [el.name]: false }
        }, {}),
      )
    }
  }, [filterData.filterer.savedFilters])

  useLayoutEffect(() => {
    const paginationHeight = data ? 54 : 0
    const html = document.querySelector('html')
    const resizeFilter = () => {
      if (filterRef.current && tableRef.current && filterContainerRef.current) {
        const htmlScrollTop = html?.scrollTop || 0
        const windowInnerHeight = window.innerHeight
        const fullScreenScrollHeight = htmlScrollTop + windowInnerHeight
        const defaultFilterOffset =
          filterContainerRef.current.offsetHeight -
          filterRef.current.offsetHeight -
          DEFAULT_INDENT -
          FILTER_INSIDE_PADDING

        const tableOffset = getFullOffsetOfElement(tableRef.current)
        const filterFullOffset = getFullOffsetOfElement(filterRef.current) + defaultFilterOffset
        const offsetFromBottom = Math.max(
          fullScreenScrollHeight +
            paginationHeight -
            tableRef.current.scrollHeight -
            tableOffset -
            DEFAULT_INDENT,
          0,
        )

        if (isXL) {
          if (tableRef.current.clientHeight < windowInnerHeight - tableOffset - DEFAULT_INDENT) {
            filterRef.current.style.maxHeight = `${
              fullScreenScrollHeight - filterFullOffset - (isMenuShown ? MARGIN_BOTTOM_XL : 0)
            }px`
          } else {
            filterRef.current.style.maxHeight = `${
              fullScreenScrollHeight - filterFullOffset - offsetFromBottom
            }px`
          }
        } else {
          filterRef.current.style.maxHeight = 'initial'
        }
      }
    }
    resizeFilter()
    window.addEventListener('scroll', resizeFilter)
    window.addEventListener('resize', resizeFilter)
    return () => {
      window.removeEventListener('scroll', resizeFilter)
      window.removeEventListener('resize', resizeFilter)
    }
  }, [data, isMenuShown, isXL])

  useEffect(() => {
    if (filterData) {
      const noRenderArr = filterData.filterable
        .filter(el => el.shouldRender === false)
        .map(el => el.field)
      filterData.filterer.setFilter(prev => {
        noRenderArr.forEach(el => {
          prev[el] = getDefaultFilterValue(el as string)
        })
        return prev
      })
    }
  }, [filterData])

  const onDeleteSavedFilter = async (filter: ContactFilterSavedInterface) => {
    await filterData.onDeleteSavedFilter(filter)
    if (savedFilters.length === 0) {
      setSelectedIndex(0)
    }
  }

  return (
    <>
      <div className={`mb-5 flex justify-between`}>
        {data?.items.length === 0 || counter === false ? (
          <h1 className="flex h-[42px] items-center whitespace-nowrap">{title}</h1>
        ) : (
          <div className={`flex items-center gap-1`}>
            {counter ? (
              <h2 className={'text-text-primary whitespace-nowrap text-[22px] font-bold'}>
                {counter.count}
              </h2>
            ) : (
              <Loader type={'dark'} small />
            )}
            <h1 className="flex h-[42px] items-center whitespace-nowrap">{title}</h1>
          </div>
        )}
        <div className={`flex gap-3`}>
          {filterData && (
            <PrimaryButton
              onClick={() => setIsFilterShown(!isFilterShown)}
              className="flex items-center xl:hidden"
            >
              <FilterIcon className="h-[15px] w-[15px]" />
            </PrimaryButton>
          )}
          {customHeaderElement && <div>{customHeaderElement}</div>}
        </div>
      </div>
      {
        <div className={`flex ${isFilterShown ? 'gap-5' : 'gap-0 xl:gap-5'} flex-col xl:flex-row`}>
          <div
            ref={filterContainerRef}
            className={`top-5 flex w-full flex-col overflow-x-hidden rounded-lg lg:overflow-visible xl:sticky xl:inset-0 xl:h-fit xl:w-fit xl:min-w-[307px] xl:max-w-[400px] xl:shrink-0 xl:overflow-x-hidden`}
          >
            <div
              className={`bg-brand-primary-subtle justify-center ${
                isFilterShown ? 'flex' : 'hidden xl:flex'
              }`}
            >
              <HeadlessUI.TabGroup selectedIndex={selectedIndex} onChange={setSelectedIndex}>
                <HeadlessUI.TabList className={'flex w-full'}>
                  <HeadlessUI.Tab
                    className={({ selected }) =>
                      `${
                        selected
                          ? 'text-text-primary border-b-brand-primary border-b-2'
                          : 'border-b-2 border-b-transparent'
                      } main-transition-colors w-full px-7 pb-4 pt-4 outline-hidden`
                    }
                  >
                    <span className="font-inter whitespace-nowrap text-[17px] font-bold">
                      {t('global.filter_by')}
                    </span>
                  </HeadlessUI.Tab>
                  <HeadlessUI.Tab
                    disabled={
                      !filterData.filterer.savedFilters ||
                      filterData.filterer.savedFilters.length === 0
                    }
                    className={({ selected }) =>
                      `${
                        selected
                          ? 'text-text-primary border-b-brand-primary border-b-2'
                          : 'border-b-2 border-b-transparent'
                      } main-transition-colors disabled:text-text-tertiary/50 w-full px-7 pb-4 pt-4 outline-hidden`
                    }
                  >
                    <span className="font-inter whitespace-nowrap text-[17px] font-bold">
                      {t('dashboard.contacts.filter.saved_filters')}
                    </span>
                  </HeadlessUI.Tab>
                </HeadlessUI.TabList>
              </HeadlessUI.TabGroup>
            </div>
            <form
              className={`bg-background-secondary flex w-full flex-col flex-wrap justify-end gap-3 rounded-b-lg px-6 py-4 lg:flex-row xl:flex-col xl:px-2 ${
                isFilterShown ? 'flex' : 'hidden xl:flex'
              }`}
            >
              {selectedIndex === 0 ? (
                <>
                  <div
                    ref={filterRef}
                    className={'customScroll flex w-full flex-col gap-3 xl:overflow-y-auto'}
                  >
                    {filterData.filterable.map(
                      el =>
                        el.shouldRender !== false &&
                        el.onRender(
                          filterData.filterer.filter[el.field],
                          value => {
                            filterData.filterer.setFilter(prev => ({
                              ...prev,
                              [el.field]: value,
                            }))
                          },
                          filterData.filterer.filter,
                          () => filterData.filterer.setFilter(prev => prev),
                        ),
                    )}
                  </div>
                  <div className={'mt-3 flex w-full flex-col gap-3'}>
                    <div className={'flex gap-1.5'}>
                      <NeutralButton
                        type={'button'}
                        disabled={!filterData.filterer.filterFields}
                        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                          e.preventDefault()
                          setSaveModalOpened(true)
                        }}
                        className={'group w-full'}
                      >
                        <span className={'main-transition-colors group-hover:text-brand-primary'}>
                          {t('dashboard.contacts.filter.save_filter')}
                        </span>
                      </NeutralButton>
                      <CreateModal
                        needToast={false}
                        fullCaption={t('dashboard.contacts.filter.save_new_filter')}
                        creatable={[
                          {
                            field: 'name',
                            onRender: (value, onChange) => (
                              <FormInput
                                label={t('dashboard.contacts.filter.filter_name')}
                                key={'create-name'}
                                value={value}
                                onChange={e => onChange(e.target.value)}
                                error={errorSaveFilter}
                              />
                            ),
                          },
                        ]}
                        opened={saveModalOpened}
                        onClose={() => setSaveModalOpened(false)}
                        afterLeave={() => setErrorSaveFilter(undefined)}
                        defaultValues={defaultSaveFilterValues}
                        onCreate={async data => {
                          setErrorSaveFilter(undefined)
                          await filterData.onSaveNewFilter(data).catch(e => {
                            if (e instanceof BadRequest) {
                              setErrorSaveFilter(e.errors.fields.name)
                            }
                            throw e
                          })
                          setSelectedIndex(1)
                        }}
                        requiredFields={['name']}
                      />
                      <NeutralButton
                        type={'reset'}
                        disabled={!filterData.filterer.filterFields}
                        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                          e.preventDefault()
                          filterData.onResetFilters()
                        }}
                        neutralType={NeutralTypeEnum.danger}
                        className={'group w-full'}
                      >
                        <span className={'main-transition-colors group-hover:text-danger'}>
                          {t('global.reset')}
                        </span>
                      </NeutralButton>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {savedFilters.map((el, idx) => (
                    <div
                      key={el.id}
                      className={`flex w-full items-center justify-between px-4 ${
                        idx !== savedFilters.length - 1 &&
                        'border-b-text-secondary/30 border-b pb-3'
                      }`}
                    >
                      <button
                        onClick={e => {
                          e.preventDefault()
                          setSelectedIndex(0)
                          filterData.applySavedFilters(el)
                        }}
                        className={'primary-link'}
                      >
                        {el.name}
                      </button>
                      <button
                        onClick={e => {
                          e.preventDefault()
                          setDeleteModalOpened(prev => ({ ...prev, [el.name]: true }))
                        }}
                        className={'group outline-hidden'}
                      >
                        <CloseIcon
                          className={
                            'fill-text-tertiary hover:fill-text-primary group-focus-visible:fill-text-primary'
                          }
                        />
                      </button>
                      <ConfirmationModal
                        confirmationText={t(
                          'dashboard.contacts.filter.delete_filter_confirmation',
                          {
                            filter_name: el.name,
                          },
                        )}
                        opened={deleteModalOpened[el.name]}
                        onClose={() =>
                          setDeleteModalOpened(prev => ({ ...prev, [el.name]: false }))
                        }
                        onConfirm={() => onDeleteSavedFilter(el)}
                      />
                    </div>
                  ))}
                </>
              )}
            </form>
          </div>
          <div
            ref={tableRef}
            {...(isMenuShown
              ? { style: { marginBottom: isXL ? MARGIN_BOTTOM_XL : MARGIN_BOTTOM } }
              : {})}
            className={`h-fit w-full`}
          >
            {children}
          </div>
        </div>
      }
    </>
  )
}

export default ContactsListingLayout
